import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { POSTS } from '@/data/blog';
import { TEAM_AUTHOR } from '@/data/authors';
import NewsletterSignup from '@/components/NewsletterSignup';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';

const TINTS = {
  legal: 'bg-amber-100 text-amber-900',
  business: 'bg-orange-100 text-orange-900',
  hr: 'bg-emerald-100 text-emerald-900',
};

const PROD_HOST = 'https://pdf-forms-hub.emergent.host';

export default function Blog() {
  return (
    <div>
      <Helmet>
        <title>Blog — Everything PDF | Templates, contracts & doc guides</title>
        <meta name="description" content="Honest guides on NDAs, freelance contracts, resignation letters, invoices, and every common PDF document. Written from real-world experience, not legal jargon." />
        <link rel="canonical" href={`${PROD_HOST}/blog`} />
        <meta property="og:title" content="The Paperwork Blog — Everything PDF" />
        <meta property="og:description" content="Honest guides on the forms and contracts we all dread." />
        <meta property="og:image" content={`${PROD_HOST}/og-default.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${PROD_HOST}/og-default.png`} />
      </Helmet>

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">Field notes</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-[0.95]">
          The <span className="text-orange-600 italic font-serif font-medium">paperwork</span> blog.
        </h1>
        <p className="mt-5 text-stone-600 leading-relaxed max-w-2xl text-lg">
          Plain-English guides on the forms and contracts we all dread. Built from real reviews of thousands of real docs.
        </p>

        {/* Author chip */}
        <div className="mt-6 inline-flex items-center gap-3 bg-white border border-stone-200 rounded-full pl-1 pr-4 py-1">
          <div className="w-8 h-8 rounded-full bg-stone-900 text-white grid place-items-center text-xs font-bold font-mono">
            {TEAM_AUTHOR.initials}
          </div>
          <div className="text-xs">
            <div className="font-medium text-stone-900">{TEAM_AUTHOR.name}</div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid gap-6">
          {POSTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block bg-white border border-stone-200 rounded-3xl p-7 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {p.tags.map((t, j) => (
                    <span key={j} className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${TINTS[p.cover] || 'bg-stone-100 text-stone-700'}`}>{t}</span>
                  ))}
                  <span className="text-xs text-stone-400 ml-auto">{p.readTime}</span>
                </div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight">{p.title}</h2>
                <p className="text-stone-600 mt-3 leading-relaxed">{p.excerpt}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-stone-700 group-hover:text-orange-600">
                  Read post <Icons.ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="mt-12">
          <NewsletterSignup source="blog-index" variant="card" />
        </div>
      </section>
    </div>
  );
}
